<template>
  <main class="crm-deal">
    <v-dialog
      v-model="needsCreatingAdvertisingChannel"
      persistent
      content-class="appointment-creation__popup"
      max-width="fit-content">
      <v-card class="px-5 appointment-creation__popup-wrapper">
        <v-btn
          class="appointment-creation__popup__exit"
          icon
          v-on:click="onCloseCreatingAdvertisingChannelDialog">
          <v-icon color="#192a3e">mdi-close</v-icon>
        </v-btn>
        <v-card-title class="text-h7 justify-center">
          Добавление источника пациента
        </v-card-title>
        <input-text
          v-if="needsCreatingAdvertisingChannel"
          v-bind:required="needsCreatingAdvertisingChannel"
          title="Наименование"
          v-model="customAdvertisingChannel"
          v-letter-capital
          v-bind:violations="customAdvertisingChannelInputErrors" />
        <v-card-actions class="appointment-creation__popup__button-wrapper justify-center">
          <v-btn
            class="appointment-creation__popup__button-cancel"
            color="blue darken-1"
            text
            v-on:click="onCloseCreatingAdvertisingChannelDialog">Отмена</v-btn>
          <v-btn
            class="appointment-creation__popup__button-accept"
            text
            v-on:click="createAdvertisingChannel">Сохранить</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <header v-if="dealIsLoaded" class="crm-deal__header">
      <button-back />
      <div class="crm-deal__header__title">
        <h1> {{ deal.title }} </h1>
        <span> Создана: {{ deal.createdAt }} </span>
      </div>
      <div class="crm-deal__header__buttons">
        <button
          class="crm-deal__header__button-task"
          v-on:click="showTaskCreationForm"
          type="button"
        >Добавить задачу</button>
        <button
          class="crm-deal__header__button-schedule"
          type="button"
          v-on:click="onOpenSchedule"
        >Записать</button>
      </div>
    </header>
    <div class="crm-deal__container">
      <form
        v-if="dealIsLoaded"
        class="crm-deal__container__form"
        v-on:submit.prevent="onEditDeal()"
      >
        <div class="crm-deal__container__row-flex">
          <the-select
            title="Воронка"
            placeholder="Выберите воронку"
            v-bind:optional="false"
            v-bind:options="funnelOptions"
            v-bind:violations="funnelInputErrors"
            v-model="deal.funnel"
            v-on:input="resetStage" />
          <the-select
            title="Этап"
            placeholder="Выберите этап"
            v-bind:optional="false"
            v-bind:options="funnelStageOptions"
            v-bind:violations="stageInputErrors"
            v-model="deal.stage" />
        </div>
        <div class="crm-deal__container__info">
          <button
            type="button"
            class="crm-deal__container__patient"
            v-on:click="onRouteToPatient(deal.patientId)"
          >
            <span>
              {{ deal.patientName }}
              <span v-if="deal.patientAge"> ({{ deal.patientAge }}) </span>
            </span>
          </button>
          <span
            v-if="isAllowedViewPhone"
            class="crm-deal__container__phone"
          >
            {{ deal.phoneTitle }}
            <button
              v-if="isAllowedInitiateCall && deal.patientPhone"
              class="crm-deal__container__phone__icon"
              type="button"
              title="Позвонить"
              v-on:click="onInitiateCallByPatient(deal.patientId, deal.patientPhone)" />
          </span>
          <div class="crm-deal__container__note" v-if="hasLabels">
            <patient-labels v-bind:labels="this.deal.patientLabels" />
          </div>
        </div>
        <autocomplete-input
          class="crm-deal__container__row"
          label="Услуга"
          placeholder="Услуга"
          item-text="title"
          v-bind:menu-props="{ maxWidth: 'min(80%, 586px)' }"
          v-bind:clearable="true"
          v-bind:items="serviceOptions"
          v-bind:violations="serviceInputErrors"
          v-model="deal.service" />
        <autocomplete-input
          class="crm-deal__container__row"
          label="Врач"
          placeholder="Врач"
          item-text="title"
          v-bind:clearable="true"
          v-bind:items="doctorOptions"
          v-bind:violations="doctorInputErrors"
          v-model="deal.doctor" />
        <autocomplete-input
          class="crm-deal__container__row"
          label="Канал"
          placeholder="Канал"
          item-text="title"
          v-bind:items="channelOptions"
          v-bind:violations="channelInputErrors"
          v-model="deal.channel" />
        <autocomplete-input
          class="crm-deal__container__row"
          label="Источник"
          placeholder="Источник пациента"
          item-text="title"
          v-bind:clearable="true"
          v-bind:items="[...sourceOptionsWithDeleted, { title: 'Добавить источник пациента', value: 'new' }]"
          v-bind:violations="sourceInputErrors"
          v-model="deal.source" />
        <autocomplete-input
          label="Причина отказа"
          placeholder="Причина отказа"
          item-text="title"
          v-bind:clearable="true"
          v-bind:can-delete-items="true"
          v-bind:items="rejectionReasonOptions"
          v-model="deal.rejectionReason"
          v-bind:violations="rejectionReasonInputErrors"
          v-on:delete-item="onDeleteRejectionReason"
        />
        <template v-if="needsCreatingRejectionReason">
          <input-text
            v-bind:required="needsCreatingRejectionReason"
            title="Причина отказа"
            v-model="customRejectionReason"
            v-letter-capital
            v-bind:violations="rejectionReasonErrorMessageProcessed" />
        </template>
        <div class="crm-deal__container__row-flex">
          <button
            class="crm-deal__container__button-submit"
            type="submit"
          > Сохранить </button>
          <button
            class="crm-deal__container__button-cancel"
            type="button"
            v-on:click="onCancelChanges()"
          > Отмена </button>
        </div>
      </form>
      <div class="crm-deal__container__events events">
        <h3>События</h3>
        <ul
          v-if="historyList.length"
          class="events__list"
          ref="eventsList">
          <li
            v-for="(action, index) in historyList"
            v-bind:key="`history-action-${index}`"
            class="events__item"
          >
            <p class="events__item__header">
              {{ action.created_at }} {{ action.title }} {{ action.employee }}
            </p>
            <div class="events__item__description">
              <p class="events__item__wrapper">
                <span class="events__item__message">{{ action.description }}</span>
                <button
                  v-if="action.type === 'call' && action.data"
                  type="button"
                  class="events__item__button-record"
                  v-on:click="onPlayRecord(action.data)"
                  aria-label="Прослушать"
                >Прослушать</button>
              </p>
              <button
                v-if="action.type === 'note'"
                type="button"
                class="events__item__button-delete"
                v-on:click="onDeleteNote(action.id)"
                aria-label="Удалить"
              />
            </div>
          </li>
        </ul>
        <form
          class="crm-deal__container__add-note"
          v-on:submit.prevent="onAddNote">
          <textarea-length-limitation
            placeholder="Введите текст"
            v-bind:max-length="noteMaxLength"
            v-bind:rows="4"
            v-bind:violations="noteInputErrors"
            v-model="note" />
          <button
            class="crm-deal__container__button-note"
            type="submit">Добавить примечание</button>
        </form>
      </div>
    </div>
    <side-slide-transition>
      <confirm-side-dialog
        v-if="isTaskCreationFormOpen"
        v-on:cancel="closeTaskCreationForm"
        v-bind:need-confirm="needConfirmCreationForm"
        label="Создать задачу"
      >
        <form-task-creation
          v-bind:fixed-patient-id="deal.patientId"
          v-bind:patients="patientList"
          v-bind:initial-performers="performerList"
          v-bind:roles-options="roleOptions"
          v-bind:violations="createViolations"
          v-on:cancel="closeTaskCreationForm"
          v-on:create-task="onSendCreateRequest"
          v-on:task-is-changed="needConfirmCreationForm = $event"
          v-on:search-input="fetchTaskOptionsList"
        />
      </confirm-side-dialog>
    </side-slide-transition>
    <div v-if="recordFileLink" class="crm-deal__audio">
      <audio class="call-history__audio__player" autoplay controls v-bind:src="recordFileLink">
        <track kind="captions" />
      </audio>
    </div>
  </main>
</template>

<script>
import TheSelect from '@/components/TheSelect.vue';
import AutocompleteInput from '@/components/AutocompleteInput.vue';
import { builtInDialogMixin, DialogResult } from '@/components/ClinicPanelDialog.vue';
import { EditDealRequest } from '@/storage/gateways/CrmGateway';
import notifierMixin from '@/mixins/notifierMixin';
import TextareaLengthLimitation from '@/components/TextareaLengthLimitation.vue';
import ConfirmSideDialog from '@/components/ConfirmSideDialog.vue';
import FormTaskCreation from '@/components/FormTaskCreation.vue';
import SideSlideTransition from '@/components/SideSlideTransition.vue';
import ButtonBack from '@/components/ButtonBack.vue';
import PatientLabels from '@/components/PatientLabels.vue';
import callMixin from '@/mixins/callMixin';
import InputText from '@/components/InputText.vue';
import debounce from 'lodash.debounce';

/**
 * @description Разрешить получение списка ошибок для поля ввода.
 * @param {string} cause Имя поля ввода.
 * @returns {function}
 */
const resolveInputErrors = (cause) => (vm) => (
  cause in vm.inputErrors ? vm.inputErrors[cause] : []
);
/**
 * @description Максимальное количество вводимых символов для поля "Причина отказа".
 * @type {number}
 */
const MAX_LENGTH_REJECTION_REASON = 255;

/**
 * Миксин формы создания задачи
 */
const formTaskCreationMixin = {
  data() {
    return {
      /** Нужно подтверждение закрытия формы создания */
      needConfirmCreationForm: false,
      /** Открыта ли форма создания задачи */
      isTaskCreationFormOpen: false,
      /** Ошибки при создании */
      createViolations: {},
    };
  },
  methods: {
    /**
     * @description Отправить запрос на создание задачи
     * @param {TaskRequest} request
     */
    onSendCreateRequest(request) {
      const onSuccess = (message) => {
        this.notifySuccess(message);
        this.closeTaskCreationForm();
        this.fetchDealHistory();
      };
      const onFail = (error) => {
        this.createViolations = error.allCauses();
        this.notifyFail(error.getTitle());
      };
      this.$taskGateway.createTask(request, this.dealId).then((result) => {
        result.fold(onFail, onSuccess);
      });
    },
    /**
     * @description Показать форму создания задачи
     */
    showTaskCreationForm() {
      this.isTaskCreationFormOpen = true;
    },
    /**
     * @description Закрыть форму создания задачи
     */
    closeTaskCreationForm() {
      this.isTaskCreationFormOpen = false;
      this.createViolations = {};
    },
  },
};

/**
 * @description Раздел редактирования CRM-заявки.
 * @type {Vue}
 */
export default {
  components: {
    InputText,
    PatientLabels,
    ButtonBack,
    SideSlideTransition,
    FormTaskCreation,
    ConfirmSideDialog,
    TextareaLengthLimitation,
    TheSelect,
    AutocompleteInput,
  },
  computed: {
    channelInputErrors: resolveInputErrors('channel'),
    doctorInputErrors: resolveInputErrors('doctor'),
    funnelInputErrors: resolveInputErrors('funnel'),
    serviceInputErrors: resolveInputErrors('service'),
    sourceInputErrors: resolveInputErrors('source'),
    stageInputErrors: resolveInputErrors('stage'),
    noteInputErrors: resolveInputErrors('note'),
    rejectionReasonInputErrors: resolveInputErrors('rejection-reason'),
    /**
     * Проверить, были ли изменения
     *
     * @return {boolean}
     */
    isHasChanges() {
      return JSON.stringify(this.initialDeal) !== JSON.stringify(this.deal);
    },
    customRejectionReasonInputErrors: resolveInputErrors('custom-rejection-reason'),
    customAdvertisingChannelInputErrors: resolveInputErrors('custom-advertising-channel'),
    /**
     * @description Необходимо создать новую причину отказа.
     * @returns {boolean}
     */
    needsCreatingRejectionReason() {
      return this.deal.rejectionReason === 'new';
    },
    needsCreatingAdvertisingChannel() {
      return this.deal?.source === 'new';
    },
    /**
     * @description Получить обработанное сообщение об ошибке.
     * @returns {array}
     */
    rejectionReasonErrorMessageProcessed() {
      const errorMessages = this.customRejectionReasonInputErrors;
      if (errorMessages.length) {
        return this.customRejectionReason.length > MAX_LENGTH_REJECTION_REASON
          ? [`Превышена допустимая длина в ${MAX_LENGTH_REJECTION_REASON} символов`]
          : ['Некорректный формат'];
      }
      return [];
    },
    /**
     * @description Идентификатор заявки.
     * @param $route
     * @returns {number}
     */
    dealId: ({ $route }) => parseInt($route.params.id, 10),
    /**
     * @description Наличие особых меток.
     * @returns {boolean}
     */
    hasLabels() {
      return this.deal.patientLabels.length > 0;
    },
    /**
     * @description Отметки.
     * @returns {*}
     */
    labelsTitle() {
      return this.deal.patientLabels.join(', ');
    },
    /**
     * @description Этапы выбранной воронки.
     */
    funnelStageOptions() {
      return this.stageOptions.flatMap(
        (option) => (this.deal.funnel === option.funnel ? [option] : []),
      );
    },
  },
  created() {
    this.fetchDealOptions();
    this.fetchTaskOptionsList();
    this.fetchDealHistory();
  },
  data() {
    return {
      channelOptions: [],
      /**
       * @description Данные заявки.
       * @type {object}
       */
      deal: null,
      /**
       * @description Инициализированные данные заявки.
       * @type {object}
       */
      initialDeal: null,
      dealIsLoaded: false,
      doctorOptions: [],
      funnelOptions: [],
      rejectionReasonOptions: [],
      optionsIsLoaded: false,
      isAllowedViewPhone: false,
      isAllowedInitiateCall: false,
      isScrollEventList: true,
      noteMaxLength: null,
      note: '',
      /** Список исполнителей */
      performerList: [],
      /**
       * @description Опции для ролей
       * @type Array
       */
      roleOptions: [],
      patientList: [],
      serviceOptions: [],
      sourceOptions: [],
      sourceOptionsWithDeleted: [],
      stageOptions: [],
      historyList: [],
      recordFileLink: '',
      inputErrors: {},
      customRejectionReason: '',
      /**
       * @description Свой источник пациента.
       * @type {string}
       */
      customAdvertisingChannel: '',
    };
  },
  watch: {
    historyList() {
      if (this.isScrollEventList) {
        this.scrollEventsListToEnd();
        this.isScrollEventList = false;
      }
    },
    sourceOptions: {
      handler: 'setAdvertisingIdByAggregatorName',
      immediate: true,
    },
  },
  methods: {
    /**
     * @description Проскроллить список событий вниз.
     * @returns {void}
     */
    scrollEventsListToEnd() {
      this.$nextTick(() => {
        const { eventsList } = this.$refs;
        if (eventsList) {
          eventsList.scrollTop = eventsList.scrollHeight;
        }
      });
    },
    /**
     * @description Получить данные заявки на редактирование.
     * @returns {void}
     */
    fetchDeal() {
      if (!this.dealId) { return; }
      this
        .$crmGateway
        .viewDeal(this.dealId)
        .then((result) => {
          result.forEachLeft(({ title }) => { this.notifyFail(title); });
          result.forEach((deal) => {
            this.deal = deal;
            this.initialDeal = structuredClone(deal);
            this.dealIsLoaded = true;
            this.sourceOptionsWithDeleted = this.sourceOptions.slice();
            if (deal.source) {
              const exists = this.sourceOptions.some(({ value }) => value === deal.source);
              if (exists !== true) {
                this.sourceOptionsWithDeleted.push({ title: deal.sourceName, value: deal.source });
              }
            }
          });
        });
    },
    /**
     * @description Получить историю CRM-заявки.
     * @returns {void}
     */
    fetchDealHistory() {
      this
        .$crmGateway
        .viewDealHistory(this.dealId)
        .then((result) => {
          result.forEachLeft(({ title }) => { this.notifyFail(title); });
          result.forEach(({ historyList }) => {
            this.historyList = historyList;
          });
        });
    },
    /**
     * @description Получить опции заявки.
     * @param {boolean} fetchDeal
     * @returns {void}
     */
    fetchDealOptions(fetchDeal = true) {
      this.optionsIsLoaded = false;
      this
        .$crmGateway
        .viewDealOptions()
        .then((result) => {
          result.forEachLeft(({ title }) => { this.notifyFail(title); });
          result.forEach(({
            channels,
            doctors,
            funnels,
            services,
            sources,
            stages,
            isAllowedViewPhone,
            isAllowedInitiateCall,
            noteMaxLength,
            rejectionReasons,
          }) => {
            this.channelOptions = channels;
            this.doctorOptions = doctors;
            this.funnelOptions = funnels;
            this.serviceOptions = services;
            this.sourceOptions = sources;
            this.stageOptions = stages;
            this.isAllowedViewPhone = isAllowedViewPhone;
            this.isAllowedInitiateCall = isAllowedInitiateCall;
            this.noteMaxLength = noteMaxLength;
            this.optionsIsLoaded = true;
            this.rejectionReasonOptions = rejectionReasons;
            this.rejectionReasonOptions.push({ title: 'Свой вариант', value: 'new' });
            if (fetchDeal) {
              this.fetchDeal();
            } else {
              this.sourceOptionsWithDeleted = this.sourceOptions.slice();
            }
          });
        });
    },
    /**
     * @description Затянуть список исполнителей
     * @param {string} searchPatientQuery
     */
    fetchTaskOptionsList: debounce(function _(searchPatientQuery = '') {
      const onSuccess = ({ performers, patients, roleOptions }) => {
        this.performerList = performers;
        this.patientList = patients;
        this.roleOptions = roleOptions;
      };
      this.$taskGateway.getTaskOptions(searchPatientQuery ?? '').then((result) => {
        result.fold(() => {}, onSuccess);
      });
    }, 500),
    /**
     * @description Обработать событие добавления примечания.
     */
    onAddNote() {
      this.inputErrors = {};
      this
        .$crmGateway
        .addDealNote(this.dealId, this.note)
        .then((result) => {
          result.forEachLeft((error) => {
            const failurePairs = (error.causes || []).map((cause) => [cause.source, cause.details]);
            this.inputErrors = Object.fromEntries(failurePairs);
            if (failurePairs.length === 0) {
              this.clarifyAndNotifyFail(error.interaction)(
                error.title,
                error.detail || '',
                error.telegramLink,
                error.whatsAppLink,
              );
            }
          });
          result.forEach(({ title }) => {
            this.notifySuccess(title);
            this.note = '';
            this.fetchDealHistory();
          });
        });
    },
    /**
     * @description Удаление примечания.
     * @param {string} id
     * @returns {void}
     */
    async onDeleteNote(id) {
      const dialog = await this.showAcceptDialog('Вы действительно хотите удалить примечание?');
      if (dialog.result !== DialogResult.YES) {
        return;
      }
      this
        .$crmGateway
        .deleteCrmDealNoteByAction(id)
        .then(
          (result) => {
            result.forEachLeft(({ title }) => { this.notifyFail(title); });
            result.forEach(({ title }) => {
              this.notifySuccess(title);
              this.fetchDealHistory();
            });
          },
        );
    },
    /**
     * @description Обработать удаление причины отказа.
     * @param  {number} id
     * @returns {Promise<void>}
     */
    async onDeleteRejectionReason(id) {
      const dialog = await this.showAcceptDialog('Вы действительно хотите удалить причину отказа?');
      if (dialog.result !== DialogResult.YES) {
        return;
      }
      this
        .$crmGateway
        .deleteRejectionReason(id)
        .then(
          (result) => {
            result.forEachLeft(({ title }) => { this.notifyFail(title); });
            result.forEach((title) => {
              this.notifySuccess(title);
              this.fetchDealOptions();
            });
          },
        );
    },
    /**
     * @description Обработать отмену изменений.
     * @returns {void}
     */
    async onCancelChanges() {
      if (this.isHasChanges) {
        const dialog = await this.showAcceptDialog('Вы действительно хотите отменить изменения?');
        if (dialog.result !== DialogResult.YES) {
          return;
        }
      }
      this.fetchDeal();
    },
    /**
     * @description Обработать сохранение изменений.
     * @returns {void}
     */
    onEditDeal() {
      const request = new EditDealRequest(
        this.deal.channel,
        this.dealId,
        this.deal.doctor,
        this.deal.funnel,
        this.deal.service,
        this.deal.source,
        this.deal.stage,
        this.deal.stage !== this.initialDeal.stage,
        this.deal.funnel !== this.initialDeal.funnel,
        this.needsCreatingRejectionReason
          ? this.customRejectionReason
          : this.deal.rejectionReason,
        this.needsCreatingRejectionReason,
      );
      this.inputErrors = {};
      this
        .$crmGateway
        .editDeal(request)
        .then((result) => {
          result.forEachLeft(async (error) => {
            const failurePairs = (error.causes || []).map((cause) => [cause.source, cause.details]);
            this.inputErrors = Object.fromEntries(failurePairs);
            if (failurePairs.length === 0) {
              if (error.change_funnel) {
                const dialog = await this.showAcceptDialog(error.title);
                if (dialog.result === DialogResult.YES) {
                  this.onRouteToDeal(error.deal_id);
                }
              } else {
                this.clarifyAndNotifyFail(error.interaction)(
                  error.title,
                  error.detail || '',
                  error.telegramLink,
                  error.whatsAppLink,
                );
              }
            }
          });
          result.forEach(({ title }) => {
            this.notifySuccess(title);
            if (this.needsCreatingRejectionReason) {
              this.fetchDealOptions();
            } else {
              this.fetchDeal();
            }
            this.fetchDealHistory();
          });
        });
    },
    /**
     * @description Обработать переход на расписание.
     * @returns {void}
     */
    onOpenSchedule() {
      if (this.deal.patientIsArchive) {
        this.notifyFail('Установлен запрет записи на прием');
        return;
      }
      this.$router.push({
        name: 'ClinicPanelAppointmentScheduleEmployees',
        params: { patientId: Number(this.deal.patientId), dealId: Number(this.dealId) },
      });
    },
    onPlayRecord(recordFileLink) {
      this.recordFileLink = recordFileLink;
    },
    /**
     * @description Обработать переход на страницу пациента.
     * @param patientId
     */
    onRouteToPatient(patientId) {
      this.$router.push({
        name: 'ClinicPanelPatientBasic',
        params: { id: patientId },
      });
    },
    /**
     * @description Обработать переход на страницу заявки.
     * @param dealId
     */
    onRouteToDeal(dealId) {
      this.$router.push({
        name: 'ClinicPanelCrmDeal',
        params: { id: dealId },
      });
    },
    /**
     * @description Сбросить выбор этапа.
     * @returns {void}
     */
    resetStage() {
      this.deal.stage = '';
    },
    /**
     * @description Создать источник пацинета.
     * @returns {void}
     */
    createAdvertisingChannel() {
      this.onCreateAdvertisingChannel(this.customAdvertisingChannel);
    },
    /**
     * Обработать событие закрытие окна создания канала.
     * @returns {void}
     */
    onCloseCreatingAdvertisingChannelDialog() {
      this.customAdvertisingChannel = '';
      this.deal.source = null;
    },
    /**
     * @description Установить идентификатор источника по названию агрегатора.
     * @returns {void}
     */
    setAdvertisingIdByAggregatorName() {
      const isSelected = this.deal?.source !== 'new';
      if (isSelected) { return; }
      if (this.sourceOptions.length && this.customAdvertisingChannel) {
        const aggregatorName = this.customAdvertisingChannel.toLowerCase();
        const candidate = this
          .sourceOptions
          .find((channel) => channel.title.toLowerCase().includes(aggregatorName));
        this.deal.source = candidate?.value || null;
      }
    },
    /**
     * @description Обработать создание рекламного канала.
     * @returns {void}
     */
    async onCreateAdvertisingChannel(channel) {
      const onFail = (error) => {
        this.notifyFail(error.title);
        const failurePairs = error.causes.map(
          (cause) => ['custom-advertising-channel', cause.details],
        );
        this.inputErrors = Object.fromEntries(failurePairs) || {};
      };
      const onSuccess = ({ message }) => {
        this.notifySuccess(message);
        this.fetchDealOptions(false);
      };
      const either = await this.$advertisingChannelGateway.create(channel);
      either.fold(onFail, onSuccess);
    },
  },
  mixins: [
    builtInDialogMixin,
    notifierMixin,
    formTaskCreationMixin,
    callMixin,
  ],
  name: 'ClinicPanelCrmDeal',
};
</script>

<style scoped lang="scss">
@use "@/scss/buttons";

::v-deep .task-creation__button{
  margin-top: 0;
}

.crm-deal {
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  &__audio {
    position: sticky;
    bottom: 0;
    left: 0;
    right: 0;
    display: flex;
    &__player {
      width: 100%;
    }
  }
  &__header {
    width: 100%;
    padding: 36px 27px 34px 37px;
    display: grid;
    justify-content: space-between;
    gap: .5rem;
    border-bottom: 2px solid #eaeff3;
    &__title {
      grid-row: 2/3;
      width: max-content;
      font-size: 32px;
      line-height: 40px;
      font-weight: 600;
      color: #192a3e;
      h1 {
        font-weight: 600;
      }
      span {
        color: #707683;
        font-size: 14px;
        font-weight: 400;
        line-height: 17px;
        margin: 10px 0;
      }
    }
    &__buttons {
      display: grid;
      grid-template-columns: 1.2fr 1fr;
      grid-row: 2/3;
      max-width: 550px;
      width: 100%;
      column-gap: 10px;
      align-items: center;
    }
    &__button-schedule {
      @include buttons.btn-blue(15rem, 3rem, 1rem);
      margin-left: auto;
    }
    &__button-task {
      @include buttons.btn-transparent(15rem, 3rem, 1rem);
      @include buttons.btn-transparent-add(1rem);
      margin-left: auto;
    }
  }
  &__container {
    display: grid;
    grid-template-columns: 2fr 3fr;
    gap: 10px;
    flex-grow: 1;
    width: 100%;
    padding: 0 20px 0 23px;
    overflow-y: auto;
    &__form {
      border-right: 2px solid #eaeff3;
      padding: 0 20px;
    }
    &__row-flex {
      display: flex;
      margin-top: 30px;
      margin-bottom: 20px;
      gap: 10px 5px;
      .select {
        flex: 1;
        max-width: fit-content;
      }
    }
    &__info {
      display: grid;
      justify-content: start;
      gap: 6px;
      padding: 20px 0 40px;
      font-family: "PT Sans", sans-serif;
    }
    &__patient {
      font-size: 22px;
      font-weight: 400;
      text-align: left;
      @media (hover: hover) {
        &:hover {
          color: #0076C4;
        }
      }
      &:active {
        color: #0569ac;
      }
    }
    &__note {
      color: #707683;
    }
    &__phone {
      display: flex;
      align-items: center;
      gap: 10px;
      font-size: 20px;
      font-weight: 400;
      &__icon {
        background-image: url('@/assets/img/phone-blue.svg');
        background-size: contain;
        background-position: center;
        width: 20px;
        height: 20px;
      }
    }
    &__row {
      margin: 0 0 20px 0;
    }
    &__button-submit {
      @include buttons.btn-blue(174px, 48px, 16px);
    }
    &__button-cancel {
      @include buttons.btn-transparent(174px, 48px, 16px);
    }
    &__button-note {
      @include buttons.btn-transparent(200px, 48px, 16px);
    }
    &__events {
      display: grid;
      grid-template-rows: auto 1fr auto;
      position: relative;
      padding: 30px 20px;
    }
    .events {
      h3 {
        margin-bottom: 8px;
        font-family: "PT Sans", sans-serif;
        font-weight: 600;
      }
      &__list {
        display: grid;
        gap: 14px;
        margin: 0;
        padding: 0;
        padding-bottom: 20px;
        height: fit-content;
        max-height: 430px;
        list-style: none;
        overflow-y: auto;
      }
      &__item {
        height: fit-content;
        &__header {
          margin-bottom: 7px;
          font-size: 12px;
          line-height: 14px;
          color: #8D8D8D;
        }
        &__description {
          display: flex;
          align-items: center;
          gap: 22px;
          margin: 0;
          padding: 8px;
          font-size: 16px;
          line-height: 19px;
          color: #323C47;
          background-color: #EAEFF3;
        }
        &__wrapper {
          display: flex;
          align-items: center;
          flex-wrap: wrap;
          flex-grow: 1;
          gap: 10px 22px;
          margin: 0;
        }
        &__message {
          margin-right: auto;
        }
        &__button-record {
          display: flex;
          align-items: center;
          gap: 4px;
          font-size: 16px;
          color: #0489e0;
          transition: all .3s ease 0s;
          &::before {
            content: '';
            width: 16px;
            height: 16px;
            background: url("@/assets/img/player/play.svg") no-repeat 50%;
            background-size: 16px;
          }
          &:hover {
            color: #0076c4;
          }
        }
        &__button-delete {
          flex-shrink: 0;
          width: 15px;
          height: 15px;
          background: url("@/assets/img/exit-grey.svg") no-repeat 50%;
          background-size: 15px;
        }
      }
    }
    &__add-note {
      grid-row: 3 / 4;
    }
  }
}

@media screen and (max-width: 1530px) {
  .crm-deal {
    &-tabs {
      flex-wrap: wrap;
      gap: 5px 20px;
      border-bottom: none;
      &__item {
        padding-bottom: 3px;
      }
    }
  }
}

@media screen and (max-width: 1310px) {
  .crm-deal-profile__head {
    &__row {
      flex-direction: column-reverse;
      gap: 20px;
    }
  }
}

@media screen and (min-width: 960px){
  .tabs-fixed {
    background-color: white;
    position: sticky;
    z-index: 2;
    top: -40px;
    padding: 10px 0 0 0;
  }
}

@media screen and (max-width: 780px) {
  .crm-deal {
    &__header {
      &__buttons {
        display: flex;
        flex-wrap: wrap;
        row-gap: 10px;
        grid-row: 3/4;
      }
    }
  }
}

@media screen and (max-width: 650px) {
  .crm-deal {
    &__header {
      flex-direction: column;
      &__buttons {
        display: flex;
      }
      &__button-schedule {
        margin-left: 0;
        width: 12rem;
      }
      &__button-task {
        margin-left: 0;
        width: 12rem;
      }
    }
    &__container {
      grid-template-columns: 1fr;
      &__form {
        border-right: none;
      }
    }
  }
}

@media screen and (max-width: 480px) {
  .crm-deal {
    &-tabs {
      &__item {
        font-size: 16px;
        right: 30px;
      }
    }
    &__header {
      grid-template-columns: 1fr;
      &__button-schedule,
      &__button-task {
        width: 100%;
      }
    }
    &__container {
      &__row-flex {
        flex-wrap: wrap;
        .select {
          flex: auto;
        }
        button {
          width: 100%;
        }
      }
    }
  }
}
</style>
